// =============================================================================
// FILE: packages/renderer/src/hooks/useFris.ts
// PURPOSE: TanStack Query hook for FRIS (Feature Readiness Indicator System).
// =============================================================================

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

export type ReadinessLevel = 'green' | 'yellow' | 'red';

export interface DependencyGate {
  id:     string;
  label:  string;
  ok:     boolean;
  reason?: string;
}

export interface FeatureReadiness {
  id:           string;
  label:        string;
  service:      string;
  status:       ReadinessLevel;
  reason?:      string;
  dependencies: DependencyGate[];
  setupSteps?:  string[];
  setupRoute?:  string;
  lastChecked:  number;
}

export function useFrisStatus(pollMs = 30_000) {
  return useQuery<FeatureReadiness[]>({
    queryKey: ['fris', 'status'],
    queryFn:  () => call<undefined, FeatureReadiness[]>(IPC.FRIS_STATUS),
    refetchInterval: pollMs,
    staleTime: 10_000,
  });
}

export function useFrisRecompute() {
  const qc = useQueryClient();
  return useMutation<FeatureReadiness | null, Error, string>({
    mutationFn: (id) => call(IPC.FRIS_RECOMPUTE, { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fris'] }),
  });
}
